/*
 * Application State should be all within this file.
 */

// Import CSV data directly using DSV plugin
import islandsData from "@/assets/Student Map Content - Islands.csv";
import { IslandHeaders } from "@/types/index.ts";
import {
  enrichAllIslands,
  enrichSingleIsland
} from "@/helpers/islandEnrichment.js";

const app = {
  state: () => ({
    // Map data - populated from CSV and merged with student datastore entries
    // Initialize with CSV data, adding empty Reports array to match EnrichedIsland type
    islands: islandsData.map((island) => ({
      ...island,
      Reports: []
    })),

    // UI state
    selectedItem: null,
    // the id of the island that the current user is assigned to, null if no assignment
    assignedIslandId: null,
    // currently entered report details for current user
    report: {},
    // selected report index (0-based) for the currently selected island, null if no reports
    selectedReportIndex: null,
    // flag to track if initial enrichment has completed
    initialEnrichmentComplete: false,
    mapSettings: {
      zoomToSelected: false, // Toggle for auto-zoom on selection
      initialBounds: null // Calculated at startup from map.json
    }
  }),
  mutations: {
    SET_ISLANDS(state, islands) {
      state.islands = islands;
    },
    SET_SELECTED_ITEM(state, item) {
      state.selectedItem = item;
    },
    CLEAR_SELECTION(state) {
      state.selectedItem = null;
    },
    SET_ZOOM_TO_SELECTED(state, enabled) {
      state.mapSettings.zoomToSelected = enabled;
    },
    SET_INITIAL_BOUNDS(state, bounds) {
      state.mapSettings.initialBounds = bounds;
    },
    SET_ASSIGNED_ISLAND_ID(state, islandId) {
      state.assignedIslandId = islandId;
    },
    SET_REPORT(state, report) {
      state.report = report;
    },
    SET_SELECTED_REPORT_INDEX(state, index) {
      state.selectedReportIndex = index;
    },
    SET_INITIAL_ENRICHMENT_COMPLETE(state, complete) {
      state.initialEnrichmentComplete = complete;
    }
  },
  actions: {
    selectItem({ commit, state, rootState, rootGetters }, islandIdOrItem) {
      // Accept either islandId string or island object for backward compatibility
      let island;
      if (typeof islandIdOrItem === "string") {
        // Find island by ID
        island = state.islands.find(
          (i) => i[IslandHeaders.ID] === islandIdOrItem
        );
        if (!island) {
          console.warn(`Island with ID "${islandIdOrItem}" not found`);
          return;
        }
      } else {
        // Assume it's an island object
        island = islandIdOrItem;
      }
      commit("SET_SELECTED_ITEM", island);
      // Reset report index when island changes
      commit("SET_SELECTED_REPORT_INDEX", 0);
    },
    selectReportByIndex({ commit }, index) {
      commit("SET_SELECTED_REPORT_INDEX", index);
    },
    clearSelection({ commit }) {
      commit("CLEAR_SELECTION");
    },
    toggleZoomToSelected({ commit }, enabled) {
      commit("SET_ZOOM_TO_SELECTED", enabled);
    },
    setInitialBounds({ commit }, bounds) {
      commit("SET_INITIAL_BOUNDS", bounds);
    },
    mapFromDatastore({ commit }, payload) {
      /*
       * This is called whenever the Datastore/entry changes.
       * This is an opportunity for you to map stored values back into your state.
       */
      let _payload = {};
      try {
        _payload = JSON.parse(payload);
      } catch (e) {
        console.error("Error parsing datastore payload:", e);
        _payload = payload;
      }

      // Map datastore payload back to app state as needed
      commit("SET_ASSIGNED_ISLAND_ID", _payload.IslandId || null);
      commit("SET_REPORT", _payload.ReportDetails || {});
    },
    /**
     * Maps all entries from datastore (from readEntries) to the islands state.
     * This will structure entries into a list of island nation objects, each containing:
     * - the "id" of the island nation
     * - the "name" of the island nation for display on the map
     * - the "indigenous name" of the island nation for display on the map
     * - the limit of students that can be assigned to the island nation
     * - a list of reports that can be displayed in the details panel
     *   - each report object will have: student's onid, student's full name, student's report details
     * @param {Object} context - Vuex context
     * @param {Array} entries - Raw entries array from datastore (with onid, fullname, value fields)
     */
    mapEntriesFromDatastore({ commit, state }, entries) {
      // Enrich all islands with entries
      const enrichedIslands = enrichAllIslands(state.islands, entries);
      commit("SET_ISLANDS", enrichedIslands);

      // Also enrich the selected item if there is one
      if (state.selectedItem) {
        const enrichedSelectedItem = enrichSingleIsland(
          state.selectedItem,
          entries
        );
        commit("SET_SELECTED_ITEM", enrichedSelectedItem);
      }

      // Mark initial enrichment as complete
      if (!state.initialEnrichmentComplete) {
        commit("SET_INITIAL_ENRICHMENT_COMPLETE", true);
      }
    },
    /**
     * Enriches a single island with entries from the datastore.
     * @param {Object} context - Vuex context
     * @param {string} islandId - The island ID to enrich
     */
    enrichSingleIsland({ commit, state, rootState }, islandId) {
      // Use raw entries from datastore state (includes onid, fullname, value)
      const rawEntries = rootState.datastore?.entries || [];
      const island = state.islands.find((island) => {
        return island[IslandHeaders.ID] === islandId;
      });

      if (island) {
        const enrichedIsland = enrichSingleIsland(island, rawEntries);
        const updatedIslands = state.islands.map((island) => {
          if (island[IslandHeaders.ID] === islandId) {
            return enrichedIsland;
          }
          return island;
        });
        commit("SET_ISLANDS", updatedIslands);

        // Also update selected item if it matches
        if (
          state.selectedItem &&
          state.selectedItem[IslandHeaders.ID] === islandId
        ) {
          commit("SET_SELECTED_ITEM", enrichedIsland);
        }
      }
    },
    /**
     * Assigns the current user to an island by updating the assignedIslandId in state
     * @param {Object} context - Vuex context
     * @param {string} islandId - The island ID to assign
     */
    assignIsland({ commit }, islandId) {
      commit("SET_ASSIGNED_ISLAND_ID", islandId);
    },
    /**
     * Updates the current user's report with new data
     * @param {Object} context - Vuex context
     * @param {Object} reportData - The report data to update
     */
    updateReport({ commit }, reportData) {
      commit("SET_REPORT", reportData);
    }
  },
  getters: {
    // Data getters
    getIslands: (state) => state.islands,

    // UI state getters
    selectedItem: (state) => state.selectedItem,
    selectedReportIndex: (state) => state.selectedReportIndex,
    initialEnrichmentComplete: (state) => state.initialEnrichmentComplete,
    mapSettings: (state) => state.mapSettings,
    assignedIslandId: (state) => state.assignedIslandId,

    mapToDatastore(state) {
      /*
       * Datastore Payload is only used whenever you're using the
       * Datastore (or Datasheets). This is an object
       */

      const assignedIslandId = state.assignedIslandId
        ? state.assignedIslandId
        : state.selectedItem?.[IslandHeaders.ID];

      /**
       * @typedef {import("@/types/index").StudentEntryValue} StudentEntryValue
       * @type {StudentEntryValue}
       * The payload object should fit the StudentEntry type from src/types/index.ts
       */
      let payload = {
        IslandId: assignedIslandId,
        ReportDetails: state.report
      };

      payload = JSON.stringify(payload);
      return payload;
    }
  }
};

export default app;
